import { useEffect, useState, useRef } from 'react'
import cn from 'classnames'
import ReactCSSTransitionReplace from 'react-css-transition-replace'
import { Icon } from 'shared/ui'
import styles from './styles.less'

const Step = ({ step, current, completed }) => {
  if (current) {
    return (
      <div className={cn(styles.StepCircle, styles['StepCircle-current'])} />
    )
  } else if (completed) {
    return (
      <div className={cn(styles.StepCircle, styles['StepCircle-completed'])}>
        <Icon
          name="check"
          color="color.monochrome.white"
          as="div"
          size={18}
          withoutPointer
        />
      </div>
    )
  }
  return (
    <div className={cn(styles.StepCircle, styles['StepCircle-pending'])}>
      <div>{step}</div>
    </div>
  )
}

function usePrevious(value, initialValue) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  if (ref.current === undefined && initialValue !== undefined) {
    return initialValue
  }
  return ref.current
}

const StepAnimator = ({ step, current, completed }) => {
  const prev = usePrevious(
    { current, completed },
    { current: false, completed: false }
  )
  const [flags, setFlags] = useState({ current, completed })
  useEffect(
    () => {
      let timer
      if (!prev.current && !prev.completed && current) {
        timer = setTimeout(() => setFlags({ current, completed }), 250)
      } else {
        setFlags({ current, completed })
      }
      return () => clearTimeout(timer)
    },
    // Adding prev to as a dependency causes a 'maximum update depth exceeded' which smokes all the memory
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, completed]
  )
  let stepKey = step
  if (flags.current) {
    stepKey += '-current'
  } else if (flags.completed) {
    stepKey += '-completed'
  }
  return (
    <div css={{ width: 24 }}>
      <ReactCSSTransitionReplace
        transitionName="progressbar-cross-fade"
        transitionEnterTimeout={1000}
        transitionLeaveTimeout={1000}
        overflowHidden={false}
        childComponent={'div'}
      >
        <Step
          key={stepKey}
          step={step}
          current={flags.current}
          completed={flags.completed}
        />
      </ReactCSSTransitionReplace>
    </div>
  )
}

const ProgressMeter = ({ step, steps, hide }) => {
  if (hide) return null
  const items = []
  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= steps; i++) {
    items.push(
      <StepAnimator
        step={i}
        current={step === i}
        completed={step > i}
        key={`step-${i}`}
      />
    )
    if (i < steps) {
      items.push(<div className={styles.Bar} key={`bar-${i}`} />)
    }
  }
  return <div className={styles.StepContainer}>{items}</div>
}

export default ProgressMeter
